import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaComments, FaEnvelopeOpen } from 'react-icons/fa';
import { useChatDock } from '../../contexts/ChatDockContext';
import ChatWindow from './ChatWindow';
import './chatdock.css';

const ChatDock = () => {
  const {
    contacts,
    sortedContacts,
    sortedRequestContacts,
    openChats,
    openChat,
    closeChat,
    minimizeChat,
    unreadCounts,
    onlineById,
  } = useChatDock();
  const minimizedChats = openChats.filter(c => c.minimized);
  const visibleChats = openChats.filter(c => !c.minimized);
  const getVarPx = (name, fallback) => {
    try {
      const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      const n = parseInt(val.replace('px', ''), 10);
      return Number.isFinite(n) ? n : fallback;
    } catch {
      return fallback;
    }
  };
  const [contactsExpanded, setContactsExpanded] = useState(true);
  const [canPortal, setCanPortal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' | 'direct' | 'group' | 'event'
  const [viewMode, setViewMode] = useState('chats'); // 'chats' | 'requests'

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setCanPortal(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = window.localStorage.getItem('vlc-chatdock-search') || '';
    if (saved) {
      setSearchTerm(saved);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('vlc-chatdock-search', searchTerm);
  }, [searchTerm]);

  const coreContacts = (sortedContacts && sortedContacts.length > 0) ? sortedContacts : contacts;
  const baseContacts = viewMode === 'requests'
    ? (sortedRequestContacts || [])
    : coreContacts;
  const chatCount = (coreContacts && coreContacts.length) || 0;
  const requestCount = (sortedRequestContacts && sortedRequestContacts.length) || 0;

  const applyCategoryFilter = (items) => {
    if (!items || items.length === 0) return [];
    if (viewMode === 'requests') return items;
    if (categoryFilter === 'direct') {
      return items.filter(c => c.type === 'direct');
    }
    if (categoryFilter === 'group') {
      return items.filter(c => c.type === 'group' || c.type === 'academy_group');
    }
    if (categoryFilter === 'event') {
      return items.filter(c => c.type === 'event');
    }
    return items;
  };

  const normalizedQuery = searchTerm.trim().toLowerCase();
  const categoryFiltered = applyCategoryFilter(baseContacts);
  const filteredContacts = normalizedQuery
    ? categoryFiltered.filter((c) => {
        const haystack = [c.name, c.email, c.description]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedQuery);
      })
    : categoryFiltered;

  const filteredDirectAll = filteredContacts.filter(c => c.type === 'direct');
  const filteredGroupsAll = filteredContacts.filter(c => c.type === 'group' || c.type === 'academy_group');
  const filteredEventsAll = filteredContacts.filter(c => c.type === 'event');

  const renderContact = (c) => {
    const unread = unreadCounts[c.id] || 0;
    const isPerson = c.type === 'direct' || c.type === 'event';
    const isGroupChat = c.type === 'group' || c.type === 'academy_group';
    return (
      <div key={`${c.type}-${c.id}`} className="chatdock-contact-item">
        <button className="chatdock-contact" title={c.name} onClick={() => openChat(c.id)}>
          <div className="chatdock-contact-avatar">
            {isGroupChat && c.group_banner_url ? (
              <img src={c.group_banner_url} alt="" />
            ) : c.avatar_url ? (
              <img src={c.avatar_url} alt="" />
            ) : (
              <div className="chatdock-avatar-fallback">{(c.name || 'U').charAt(0)}</div>
            )}
            {isPerson && onlineById?.[c.id] && <span className="chatdock-online" />}
          </div>
          {contactsExpanded && <span className="chatdock-contact-name">{c.name}</span>}
        </button>
        {unread > 0 && <span className="chatdock-badge">{unread > 9 ? '9+' : unread}</span>}
      </div>
    );
  };

  return (
    <>
      {/* Connections list - embedded in right rail */}
      <div className="chatdock-root">
        <div className="chatdock-header">
          <div className="chatdock-header-bar">
            <div className="chatdock-view-toggle" role="tablist" aria-label="Chats or requests">
              <button
                type="button"
                className={`chatdock-view-tab ${viewMode === 'chats' ? 'active' : ''}`}
                role="tab"
                aria-selected={viewMode === 'chats'}
                aria-label="Chats"
                title="Chats"
                onClick={() => setViewMode('chats')}
              >
                <FaComments aria-hidden="true" />
                {chatCount > 0 && (
                  <span className="chatdock-tab-count">{chatCount > 9 ? '9+' : chatCount}</span>
                )}
              </button>
              <button
                type="button"
                className={`chatdock-view-tab ${viewMode === 'requests' ? 'active' : ''}`}
                role="tab"
                aria-selected={viewMode === 'requests'}
                aria-label="Message requests"
                title="Message requests"
                onClick={() => setViewMode('requests')}
              >
                <FaEnvelopeOpen aria-hidden="true" />
                {requestCount > 0 && (
                  <span className="chatdock-tab-count">{requestCount > 9 ? '9+' : requestCount}</span>
                )}
              </button>
            </div>
            <button
              className="chatdock-toggle-btn"
              onClick={() => setContactsExpanded(v => !v)}
              title={contactsExpanded ? 'Collapse contacts' : 'Expand contacts'}
              aria-label={contactsExpanded ? 'Collapse contacts' : 'Expand contacts'}
            >
              {contactsExpanded ? '▾' : '▴'}
            </button>
          </div>
        </div>

        {viewMode === 'chats' && (
          <div className="chatdock-filter-icons" aria-label="Filter contacts" role="group">
            <button
              type="button"
              className={`chatdock-filter-icon ${categoryFilter === 'all' ? 'active' : ''}`}
              title="All"
              onClick={() => setCategoryFilter('all')}
            >
              ●
            </button>
            <button
              type="button"
              className={`chatdock-filter-icon ${categoryFilter === 'direct' ? 'active' : ''}`}
              title="Connections"
              onClick={() => setCategoryFilter('direct')}
            >
              👤
            </button>
            <button
              type="button"
              className={`chatdock-filter-icon ${categoryFilter === 'group' ? 'active' : ''}`}
              title="Groups"
              onClick={() => setCategoryFilter('group')}
            >
              👥
            </button>
            <button
              type="button"
              className={`chatdock-filter-icon ${categoryFilter === 'event' ? 'active' : ''}`}
              title="Events"
              onClick={() => setCategoryFilter('event')}
            >
              📅
            </button>
          </div>
        )}

        {contactsExpanded && categoryFiltered.length > 0 && (
          <div className="chatdock-search-row">
            <div className="chatdock-search-input-wrapper">
              <span className="chatdock-search-icon" aria-hidden="true">🔍</span>
              <input
                type="text"
                className="chatdock-search-input"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}

        {contactsExpanded && (
          <div className="chatdock-connections" role="list">
            {viewMode === 'requests' ? (
              categoryFiltered.length === 0 ? (
                <div className="chatdock-empty">No requests yet.</div>
              ) : (
                <>
                  <div className="chatdock-section-label">Requests ({categoryFiltered.length})</div>
                  {categoryFiltered.map(renderContact)}
                </>
              )
            ) : categoryFiltered.length === 0 ? (
              <div className="chatdock-empty">No connections yet.</div>
            ) : filteredContacts.length === 0 ? (
              <div className="chatdock-empty">No matches.</div>
            ) : categoryFilter === 'all' ? (
              <>
                {filteredDirectAll.length > 0 && (
                  <>
                    <div className="chatdock-section-label">Connections ({filteredDirectAll.length})</div>
                    {filteredDirectAll.map(renderContact)}
                  </>
                )}
                {filteredGroupsAll.length > 0 && (
                  <>
                    <div className="chatdock-section-label">Groups ({filteredGroupsAll.length})</div>
                    {filteredGroupsAll.map(renderContact)}
                  </>
                )}
                {filteredEventsAll.length > 0 && (
                  <>
                    <div className="chatdock-section-label">Events ({filteredEventsAll.length})</div>
                    {filteredEventsAll.map(renderContact)}
                  </>
                )}
              </>
            ) : categoryFilter === 'direct' ? (
              <>
                <div className="chatdock-section-label">Connections ({filteredDirectAll.length})</div>
                {filteredDirectAll.map(renderContact)}
              </>
            ) : categoryFilter === 'group' ? (
              <>
                <div className="chatdock-section-label">Groups ({filteredGroupsAll.length})</div>
                {filteredGroupsAll.map(renderContact)}
              </>
            ) : (
              <>
                <div className="chatdock-section-label">Events ({filteredEventsAll.length})</div>
                {filteredEventsAll.map(renderContact)}
              </>
            )}
          </div>
        )}
      </div>

      {/* Chat windows - fixed position bottom left, stacking from right rail */}
      {visibleChats.length > 0 && (
        canPortal
          ? createPortal(
              <div className="chatdock-windows-container mobile-single">
                {visibleChats.map((chat, index) => {
                  // Stack windows from right to left, newest on the right
                  // Position relative to minimized tray width + gap so windows begin next to minimized conversations
                  // Each window uses configured width + gap between windows
                  const trayWidth = getVarPx('--chatdock-tray-width', 50);
                  const rightGap = getVarPx('--chatdock-window-right-gap', 16);
                  const bottomGap = getVarPx('--chatdock-window-bottom-gap', 8);
                  const windowWidth = getVarPx('--chatdock-window-width', 420);
                  const windowGap = getVarPx('--chatdock-window-gap', 10);
                  const rightOffset = trayWidth + rightGap + (visibleChats.length - 1 - index) * (windowWidth + windowGap);
                  return (
                    <div 
                      key={chat.id} 
                      className="chatdock-window-wrap"
                      style={{ 
                        right: `${rightOffset}px`,
                        bottom: `${bottomGap}px`
                      }}
                    >
                      <ChatWindow
                        contact={chat}
                        onClose={() => closeChat(chat.id)}
                        onMinimize={() => minimizeChat(chat.id)}
                      />
                    </div>
                  );
                })}
              </div>,
              document.body
            )
          : (
              <div className="chatdock-windows-container mobile-single">
                {visibleChats.map((chat, index) => {
                  // Stack windows from right to left, newest on the right
                  // Position relative to minimized tray width + gap so windows begin next to minimized conversations
                  // Each window uses configured width + gap between windows
                  const trayWidth = getVarPx('--chatdock-tray-width', 50);
                  const rightGap = getVarPx('--chatdock-window-right-gap', 16);
                  const bottomGap = getVarPx('--chatdock-window-bottom-gap', 8);
                  const windowWidth = getVarPx('--chatdock-window-width', 420);
                  const windowGap = getVarPx('--chatdock-window-gap', 10);
                  const rightOffset = trayWidth + rightGap + (visibleChats.length - 1 - index) * (windowWidth + windowGap);
                  return (
                    <div 
                      key={chat.id} 
                      className="chatdock-window-wrap"
                      style={{ 
                        right: `${rightOffset}px`,
                        bottom: `${bottomGap}px`
                      }}
                    >
                      <ChatWindow
                        contact={chat}
                        onClose={() => closeChat(chat.id)}
                        onMinimize={() => minimizeChat(chat.id)}
                      />
                    </div>
                  );
                })}
              </div>
            )
      )}

      {/* Minimized tray - fixed position far right */}
      {minimizedChats.length > 0 && (
        canPortal
          ? createPortal(
              <div className={`chatdock-minimized-tray ${visibleChats.length > 0 ? 'mobile-hide-when-chat' : ''}`}>
                {minimizedChats.map((c, index) => (
                  <button
                    key={`min-${c.id}`}
                    className="chatdock-minimized-item"
                    title={c.name}
                    onClick={() => minimizeChat(c.id)}
                    style={{ 
                      position: 'absolute',
                      right: '10px',
                      bottom: `${20 + index * 50}px`
                    }}
                  >
                    {c.type === 'group' && c.group_banner_url ? (
                      <img src={c.group_banner_url} alt="" />
                    ) : c.avatar_url ? (
                      <img src={c.avatar_url} alt="" />
                    ) : (
                      <div className="chatdock-avatar-fallback">{(c.name || 'U').charAt(0)}</div>
                    )}
                    {!!unreadCounts[c.id] && (
                      <span className="chatdock-minimized-badge">
                        {unreadCounts[c.id] > 9 ? '9+' : unreadCounts[c.id]}
                      </span>
                    )}
                  </button>
                ))}
              </div>,
              document.body
            )
          : (
              <div className={`chatdock-minimized-tray ${visibleChats.length > 0 ? 'mobile-hide-when-chat' : ''}`}>
                {minimizedChats.map((c, index) => (
                  <button
                    key={`min-${c.id}`}
                    className="chatdock-minimized-item"
                    title={c.name}
                    onClick={() => minimizeChat(c.id)}
                    style={{ 
                      position: 'absolute',
                      right: '10px',
                      bottom: `${20 + index * 50}px`
                    }}
                  >
                    {c.type === 'group' && c.group_banner_url ? (
                      <img src={c.group_banner_url} alt="" />
                    ) : c.avatar_url ? (
                      <img src={c.avatar_url} alt="" />
                    ) : (
                      <div className="chatdock-avatar-fallback">{(c.name || 'U').charAt(0)}</div>
                    )}
                    {!!unreadCounts[c.id] && (
                      <span className="chatdock-minimized-badge">
                        {unreadCounts[c.id] > 9 ? '9+' : unreadCounts[c.id]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )
      )}
    </>
  );
};

export default ChatDock;
